import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  Clock,
  Lock,
  Plus,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import kgDatasetService from '../../services/kgDatasetService';
import KgDryRunReport from './KgDryRunReport';
import KgInspectPanel from './KgInspectPanel';
import KgCombinedFieldsStep from './KgCombinedFieldsStep';
import PathField from './PathField';
import styles from './KgCreateDatasetDialog.module.scss';
import {
  DRY_RUN_DEBOUNCE_MS,
  DRY_RUN_SAMPLE_SIZE,
  INSPECT_SAMPLE_SIZE,
  MAX_FACETS,
  MAX_TEXT_FIELDS,
  TIMEZONE_OPTIONS,
  combinedDraftProblems,
  combinedDraftsFromSuggestions,
  emptyCombinedDraft,
  coverageTone,
  describeError,
  dryRunBlockedReason,
  facetCandidatesFromInspect,
  facetFromCandidate,
  formatCoverage,
  formatNumber,
  mappingFromInspect,
  isUsableCombinedDraft,
  parseRecordKey,
  preprocessingWithCombined,
  readInspection,
  readRecordsFromFiles,
  recordKeyCandidatesFromInspect,
  serializeRecordKey,
  textCandidatesFromInspect,
  textFromCandidate,
  slugifyDatasetKey,
  truncateValue,
  validateRecordKey,
  wireCombinedFields,
} from './kgUtils';

const ACCEPT = '.json,application/json,text/plain';

/**
 * Create a KG dataset (screen 2).
 *
 * `POST /kg-datasets` requires a `mapping_config` up front — but nobody can
 * author one before seeing their data, and a bare "paste your JSON config" box
 * would simply go unused. So this is a three-step wizard:
 *
 *   0. name it, and choose a sample file
 *   1. INSPECT — the server reads the samples and answers with the paths that
 *      really exist, what it had to decode to get at them, and a proposed
 *      mapping. Read-only, writes nothing, needs no dataset.
 *   2. confirm the two choices that are expensive to undo — the row identity and
 *      what gets embedded — against a live dry-run.
 *
 * The inspection is the source of truth for column paths. Sniffing the shape in
 * the browser produced paths the server did not agree with as soon as anything
 * was nested inside a JSON string, so it doesn't do that any more.
 */
const KgCreateDatasetDialog = ({ open, onClose, onCreated, onError }) => {
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);
  const dryRunTimer = useRef(null);

  const [step, setStep] = useState(0);
  const [label, setLabel] = useState('');
  const [datasetKey, setDatasetKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState('');

  const [fileName, setFileName] = useState('');
  const [samples, setSamples] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [reading, setReading] = useState(false);

  const [inspection, setInspection] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectError, setInspectError] = useState(null);
  const [mapping, setMapping] = useState(null);
  // Row-identity alternatives, best first. Offered as one-click chips so the
  // common case is a single tap rather than a trip through the column list.
  const [keyCandidates, setKeyCandidates] = useState([]);
  // An "and…" row the author has opened but not filled yet. Kept OUT of the
  // mapping on purpose: serializeRecordKey drops blanks (a "" in record_key is
  // a 400 on dry-run and save), which is exactly why pushing "" into the config
  // made the Add button do nothing. The column joins record_key once picked.
  const [pendingKeyColumn, setPendingKeyColumn] = useState(false);
  // Facet candidates and what the author has actually ticked. Kept apart from
  // the mapping so unticking one doesn't lose the candidate.
  const [facetCandidates, setFacetCandidates] = useState([]);
  const [chosenFacets, setChosenFacets] = useState(() => new Set());
  // A wide export offers hundreds of candidates; without a filter the list is
  // a scroll, not a choice.
  const [facetQuery, setFacetQuery] = useState('');
  // Text candidates, and the chosen subset as path -> {embed}. All start ticked:
  // storing text costs nothing, so the decision that actually varies per field
  // is `embed`, and a `large` body arrives stored-but-not-vectorised.
  const [textCandidates, setTextCandidates] = useState([]);
  const [chosenText, setChosenText] = useState(() => new Map());
  // Combined fields: the drafts shown on their own step, and the preprocessing
  // block from the FIRST inspection — combinations are always rebuilt on top of
  // that, so going back and forth never stacks them twice.
  const [combinedDrafts, setCombinedDrafts] = useState([]);
  const [basePreprocessing, setBasePreprocessing] = useState(null);
  const [applyingCombined, setApplyingCombined] = useState(false);
  const [combinedError, setCombinedError] = useState(null);
  const [timezone, setTimezone] = useState(0);
  const [dryRun, setDryRun] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [embeddingDimensions, setEmbeddingDimensions] = useState(512);
  const [submitting, setSubmitting] = useState(false);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const reset = useCallback(() => {
    setStep(0);
    setLabel('');
    setDatasetKey('');
    setKeyTouched(false);
    setDescription('');
    setFileName('');
    setSamples(null);
    setReading(false);
    setParseError(null);
    setInspection(null);
    setInspecting(false);
    setInspectError(null);
    setMapping(null);
    setKeyCandidates([]);
    setPendingKeyColumn(false);
    setFacetCandidates([]);
    setChosenFacets(new Set());
    setFacetQuery('');
    setTextCandidates([]);
    setChosenText(new Map());
    setCombinedDrafts([]);
    setBasePreprocessing(null);
    setApplyingCombined(false);
    setCombinedError(null);
    setTimezone(0);
    setDryRun(null);
    setDryRunError(null);
    setShowAdvanced(false);
    setEmbeddingDimensions(512);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  // The key goes in the push URL, so it is slugged from the label until the user
  // takes it over. Uniqueness is only knowable from the API's 400.
  const handleLabelChange = (value) => {
    setLabel(value);
    if (!keyTouched) setDatasetKey(slugifyDatasetKey(value));
  };

  const readFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setParseError(null);
    setReading(true);
    try {
      const { records, errors, fileCount } = await readRecordsFromFiles(files);
      if (records.length === 0) {
        setSamples(null);
        setParseError(
          errors[0] ||
            'No records found. Each file should hold a record object, or an array of them.'
        );
        return;
      }
      setSamples(records);
      setFileName(
        fileCount === 1 ? files[0].name : `${formatNumber(fileCount)} files`
      );
      // Some files failing is normal on a bulk selection — report it without
      // throwing away the ones that parsed.
      setParseError(
        errors.length > 0
          ? `${errors.length} file${errors.length === 1 ? '' : 's'} couldn't be read and ${errors.length === 1 ? 'was' : 'were'} skipped. First: ${errors[0]}`
          : null
      );
    } finally {
      setReading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    readFiles(event.dataTransfer?.files);
  };

  /**
   * Step 0 → 1. Ask the server what is actually in these records.
   *
   * `preprocessing` is OMITTED rather than sent as `{}` — that is the difference
   * between "propose one for me" and "decode nothing", and on an event export
   * where every useful field is inside a JSON string, the second answer is a
   * list of three columns and a wall of nulls.
   */
  /**
   * Turn an inspection into the proposal every later step edits. Used for the
   * first inspection and again after combined fields are applied — the second
   * /inspect is what makes a combined path appear in the pickers with real
   * coverage, and marks the columns it replaced as `covered_by`.
   *
   * @param {Function} [wire] mutates the proposal before it is shown; returns
   *   any facet candidates it created so the chooser can list them.
   */
  const applyInspection = (read, wire) => {
    setInspection(read);
    const proposed = mappingFromInspect(read, {
      key: datasetKey.trim(),
      label: label.trim(),
      description: description.trim() || null,
    });
    const created = wire ? wire(proposed) || [] : [];
    if (timezone) {
      proposed.dates = (proposed.dates || []).map((d) => ({
        ...d,
        parse: { ...(d.parse || {}), tz_offset_minutes: timezone },
      }));
    }
    setMapping(proposed);
    setKeyCandidates(recordKeyCandidatesFromInspect(read));

    // The checkboxes MIRROR the proposal rather than starting empty. A combined
    // facet replaces any server candidate on the same path so the chooser and
    // the config agree on its name.
    const createdPaths = new Set(created.map((c) => c.path));
    setFacetCandidates([
      ...created,
      ...facetCandidatesFromInspect(read, proposed).filter((c) => !createdPaths.has(c.path)),
    ]);
    setChosenFacets(new Set((proposed.facets || []).map((facet) => facet.path)));

    setTextCandidates(textCandidatesFromInspect(read));
    setChosenText(
      new Map((proposed.text || []).map((entry) => [entry.path, { embed: Boolean(entry.embed) }]))
    );
    return proposed;
  };

  const goToInspect = async () => {
    setInspecting(true);
    setInspectError(null);
    setStep(1);
    try {
      const response = await kgDatasetService.inspect(
        samples.slice(0, INSPECT_SAMPLE_SIZE)
      );
      const read = readInspection(response);
      applyInspection(read);
      setBasePreprocessing(response?.preprocessing || null);
      // Ticked by default exactly where the server recommends.
      // The list IS the rule: the server only includes an entry when it is
      // worth a screen (merging adds coverage, or no single column can identify
      // a record). Empty list → the step is skipped. Every entry is shown —
      // including an identifier-only one, without which there is no key.
      setCombinedDrafts(combinedDraftsFromSuggestions(read.combinedFields));
      setCombinedError(null);
    } catch (e) {
      setInspectError(describeError(e, "Couldn't read these records."));
    } finally {
      setInspecting(false);
    }
  };

  // Keyed on the UI's own drafts, never on the latest response: accepted
  // combinations disappear from the re-inspect's list, and an empty list there
  // must not hide what the author already chose.
  const hasCombinedStep = combinedDrafts.length > 0;

  /** → Check the mapping. Dry-run the proposal before anything is created. */
  const goToReview = (cfg = mapping) => {
    setStep(3);
    if (cfg) runDryRun(cfg);
  };

  /** Step 1 → combined fields when there are any to show, otherwise straight on. */
  const leaveInspectStep = () => {
    if (hasCombinedStep) setStep(2);
    else goToReview();
  };

  /**
   * Step 2 → 3. Save the ticked combinations into preprocessing, inspect AGAIN
   * with it, and wire each one in by its `use_as`.
   *
   * The second /inspect is not optional: it is what puts `normalized.email`
   * into fields[] with real coverage, so the identity, date and facet pickers
   * can offer it — and what marks the columns it replaced as covered.
   */
  const applyCombined = async () => {
    const usable = combinedDrafts.filter(isUsableCombinedDraft);
    // Nothing to combine: rebuild from the untouched first inspection, in case
    // an earlier pass through this step had combined something.
    setApplyingCombined(true);
    setCombinedError(null);
    try {
      const preprocessing = preprocessingWithCombined(basePreprocessing, usable);
      const response = await kgDatasetService.inspect(samples.slice(0, INSPECT_SAMPLE_SIZE), {
        preprocessing,
      });
      const read = readInspection(response);
      const proposed = applyInspection(read, (cfg) => {
        // The paths are only valid next to the block that derives them. Keep
        // ours if the response did not echo it back.
        const names = preprocessing.fields.map((f) => f.name);
        if (
          !cfg.preprocessing ||
          !names.every((n) => (cfg.preprocessing.fields || []).some((f) => f.name === n))
        ) {
          cfg.preprocessing = preprocessing;
        }
        return wireCombinedFields(cfg, usable, read.namespace);
      });
      goToReview(proposed);
    } catch (e) {
      setCombinedError(describeError(e, "Couldn't apply the combined fields."));
    } finally {
      setApplyingCombined(false);
    }
  };

  /**
   * Open the step by hand when the server suggested nothing. The backend can
   * combine any fields; it just doesn't detect everything — a customer name
   * stored under different field names, say.
   */
  const combineManually = () => {
    setCombinedDrafts((prev) => (prev.length ? prev : [emptyCombinedDraft()]));
    setStep(2);
  };

  const goBack = () => {
    if (step === 3) setStep(hasCombinedStep ? 2 : 1);
    else setStep(Math.max(0, step - 1));
  };

  /**
   * One control for every date, because it is one decision. The server proposes
   * an offset of 0 and the consequence of leaving it wrong is invisible until
   * someone asks for "last month" and gets the wrong records.
   */
  const applyTimezone = (offset) => {
    setTimezone(offset);
    editMapping((next) => {
      next.dates = (next.dates || []).map((date) => ({
        ...date,
        parse: { ...(date.parse || {}), tz_offset_minutes: offset },
      }));
    });
  };

  const runDryRun = useCallback(
    async (cfg) => {
      // record_key is mandatory server-side: sending "" is a 400, not a report.
      // Better to say what is missing than to spend a round trip being told.
      if (dryRunBlockedReason(cfg)) {
        setDryRun(null);
        setDryRunError(null);
        setDryRunning(false);
        return;
      }
      setDryRunning(true);
      setDryRunError(null);
      try {
        const response = await kgDatasetService.dryRun(
          cfg,
          samples.slice(0, DRY_RUN_SAMPLE_SIZE)
        );
        setDryRun(response);
      } catch (e) {
        setDryRunError(describeError(e, "Couldn't validate this mapping against your sample."));
      } finally {
        setDryRunning(false);
      }
    },
    [samples]
  );

  /**
   * Edits on this step re-check against the sample, debounced. Dry-run writes
   * nothing, so this is free.
   */
  const editMapping = (updater) => {
    setMapping((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      updater(next);
      if (dryRunTimer.current) clearTimeout(dryRunTimer.current);
      dryRunTimer.current = setTimeout(() => runDryRun(next), DRY_RUN_DEBOUNCE_MS);
      return next;
    });
  };

  /**
   * Tick or untick a facet candidate. The mapping's `facets` are rebuilt from
   * the chosen set each time, so the order stays the candidate order and
   * order_index stays contiguous.
   */
  const toggleFacet = (path) => {
    setChosenFacets((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      editMapping((cfg) => {
        cfg.facets = facetCandidates
          .filter((c) => next.has(c.path))
          .map((c, i) => facetFromCandidate(c, i));
      });
      return next;
    });
  };

  /**
   * Rebuild `text[]` from the chosen map, in candidate order.
   *
   * Candidate order is the server's, which puts the title-shaped field first —
   * and `priority` decides section order in the assembled document the model
   * reads, so keeping the two aligned is what makes a chunk attributable.
   */
  const applyTextChoices = (chosen) =>
    editMapping((cfg) => {
      cfg.text = textCandidates
        .filter((c) => chosen.has(c.path))
        .map((c, i) => textFromCandidate({ ...c, embed: chosen.get(c.path).embed }, i));
    });

  /** Include or drop a text field. Re-ticking restores the candidate default. */
  const toggleTextField = (path) => {
    setChosenText((prev) => {
      const next = new Map(prev);
      if (next.has(path)) next.delete(path);
      else {
        const candidate = textCandidates.find((c) => c.path === path);
        next.set(path, { embed: Boolean(candidate?.embed) });
      }
      applyTextChoices(next);
      return next;
    });
  };

  /** The money setting: vectorised, or stored-and-readable only. */
  const toggleTextEmbed = (path) => {
    setChosenText((prev) => {
      const current = prev.get(path);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(path, { embed: !current.embed });
      applyTextChoices(next);
      return next;
    });
  };

  /**
   * Back to exactly what the server recommended.
   *
   * `recommended`, not "everything we had no objection to" — those are wildly
   * different numbers on a wide export (23 against 96 on one real dataset), and
   * conflating them is how a reset button quadruples the facet list.
   */
  const chooseSuggestedFacets = () => {
    const picked = recommendedFacets.slice(0, MAX_FACETS);
    const next = new Set(picked.map((c) => c.path));
    setChosenFacets(next);
    editMapping((cfg) => {
      cfg.facets = picked.map((c, i) => facetFromCandidate(c, i));
    });
  };

  const setRecordKeyAt = (index, value) =>
    editMapping((next) => {
      const list = parseRecordKey(next.record_key);
      list[index] = value;
      next.record_key = serializeRecordKey(list.filter(Boolean));
    });

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const body = {
        key: datasetKey.trim(),
        label: label.trim(),
        mapping_config: mapping,
      };
      if (description.trim()) body.description = description.trim();
      if (Number(embeddingDimensions) !== 512) {
        body.embedding_dimensions = Number(embeddingDimensions);
      }
      const dataset = await kgDatasetService.createDataset(body);
      // Hand the sample on to the mapping editor. Capped, because it travels in
      // router state and only the first couple of hundred records are ever used
      // for column suggestions and the live check.
      onCreated?.(dataset, {
        records: samples.slice(0, 200),
        total: samples.length,
        // Only offer the one-click load when we still hold everything that was
        // read. Loading a silent 200-record subset of a big file would be worse
        // than not offering it at all.
        complete: samples.length <= 200,
      });
    } catch (e) {
      // 400s here are user-facing and specific — a duplicate key, or a mapping
      // problem the client validator didn't catch. Show it verbatim.
      onError?.(describeError(e, "Couldn't create this dataset."));
    } finally {
      setSubmitting(false);
    }
  };

  const step0Valid = label.trim() && datasetKey.trim() && samples?.length > 0 && !reading;
  const recordKeyPaths = mapping ? parseRecordKey(mapping.record_key) : [];
  const recordKeyError = mapping ? validateRecordKey(recordKeyPaths) : null;
  const inspectFields = inspection?.fields || [];
  // What the SERVER recommended. Everything about the reset button keys off this
  // and never off "candidates with no local objection".
  const recommendedFacets = facetCandidates.filter((c) => c.recommended);
  // The filter applies to EVERY row, ticked or not. Pinning ticked rows made the
  // search look broken: with 23 selected, they filled the list and the matches
  // sat below them. Hidden selections are counted instead, never dropped.
  const facetQueryNorm = facetQuery.trim().toLowerCase();
  const visibleFacetCandidates = facetQueryNorm
    ? facetCandidates.filter((c) =>
        [c.path, c.name, c.label].some((v) =>
          String(v || '').toLowerCase().includes(facetQueryNorm)
        )
      )
    : facetCandidates;
  const hiddenChosenCount = facetQueryNorm
    ? [...chosenFacets].filter((path) => !visibleFacetCandidates.some((c) => c.path === path))
        .length
    : 0;
  // True while the ticked set is already exactly that — the reset button has
  // nothing to do then, and offering it reads as "you have not done this yet"
  // over a selection that is already correct.
  const recommendedFacetsChosen =
    recommendedFacets.length === chosenFacets.size &&
    recommendedFacets.every((c) => chosenFacets.has(c.path));
  // A shortlist with entries but none marked is the server declining to choose,
  // not an empty shortlist. It has to be surfaced, because silently promoting
  // the first row would pick the dataset's upsert key for the customer.
  const keyNeedsExplicitChoice =
    keyCandidates.length > 0 && !keyCandidates.some((c) => c.recommended);
  // record_key is mandatory server-side. Everything downstream — the dry-run and
  // the create itself — is gated on it rather than left to collect a 400.
  const dryRunBlocked = mapping ? dryRunBlockedReason(mapping) : 'Inspect your data first.';
  // Duplicate row identities now come back in the dry-run report, over the same
  // records — so there is one place they are reported, not two that can disagree.

  const stepTitle =
    step === 0
      ? 'New dataset'
      : step === 1
        ? 'What’s in your data'
        : step === 2
          ? 'Combined fields'
          : 'Check the mapping';
  const combinedProblems = combinedDraftProblems(combinedDrafts);
  const combinedBlocked = Object.keys(combinedProblems).length > 0;

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => !submitting && onClose()}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(24, 24, 27, 0.4)', backdropFilter: 'blur(2px)' },
          },
        }}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            {step > 0 && (
              <IconButton size="small" onClick={goBack} aria-label="Back">
                <ChevronLeft size={18} />
              </IconButton>
            )}
            <Typography component="h3" variant="h6" className={styles.title}>
              {stepTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose()}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
            disabled={submitting}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {step === 0 ? (
            <div className={styles.form}>
              <Typography variant="body2" className={styles.lead}>
                A dataset holds structured records with the fields worth filtering on declared as
                facets. Give it a name and a sample of the data, and the mapping is drafted for
                you.
              </Typography>

              <TextField
                label="Name"
                value={label}
                onChange={(e) => handleLabelChange(e.target.value)}
                fullWidth
                size="small"
                autoFocus
                placeholder="Legal &amp; Tax News"
              />

              <TextField
                label="Key"
                value={datasetKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setDatasetKey(slugifyDatasetKey(e.target.value));
                }}
                fullWidth
                size="small"
                helperText="Used in the push URL, and unique to you. Letters, digits and underscores."
                InputProps={{ className: styles.monoInput }}
              />

              <TextField
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />

              {/* Sample file. Nothing is uploaded — the file is read in the
                  browser, and only the first few records ever leave it, as a
                  dry-run that writes nothing. */}
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Sample data</span>
                <span className={styles.fieldHelp}>
                  Read here in your browser to draft the mapping. This does not load your data —
                  you do that in the next step, and you’ll be offered these same records.
                </span>
                <div
                  className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                >
                  <UploadCloud size={30} color="var(--text-secondary)" />
                  <span className={styles.dropTitle}>
                    {reading
                      ? 'Reading…'
                      : fileName || 'Drop your JSON here, or click to choose'}
                  </span>
                  <span className={styles.dropHint}>
                    {samples
                      ? `${formatNumber(samples.length)} records read — nothing has been uploaded`
                      : 'One file with an array, or many files with one record each. A few dozen records is plenty.'}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  // Per-record exports are one file per row, so selecting a
                  // whole folder's worth at once has to work.
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    readFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                {parseError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {parseError}
                  </Alert>
                )}
              </div>
            </div>
          ) : step === 1 ? (
            /* ── Inspect: what the server can actually see in these records ── */
            <div className={styles.form}>
              {inspecting ? (
                <div className={styles.dryRunLoading}>
                  <CircularProgress size={18} />
                  <span>
                    Reading {formatNumber(Math.min(samples?.length || 0, INSPECT_SAMPLE_SIZE))} of
                    your records…
                  </span>
                </div>
              ) : inspectError ? (
                <Alert severity="error">
                  {inspectError}
                  <br />
                  Go back and try a different file, or a smaller selection.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" className={styles.lead}>
                    Nothing has been written and no dataset exists yet. This is what the platform
                    can see in your records, and the mapping it suggests from them — every part of
                    it stays editable afterwards.
                  </Typography>
                  <KgInspectPanel inspection={inspection} />

                  {/* Every line here is a PROPOSAL, and the wording has to say
                      so. "15 filterable fields" reads as a property the dataset
                      already has; it is a count of suggestions nobody has agreed
                      to yet, and the next step is where they are confirmed. */}
                  {mapping && (
                    <div className={styles.summary}>
                      <div className={styles.summaryHead}>
                        <span className={styles.summaryHeadTitle}>Suggested mapping</span>
                        <span className={styles.summaryHeadHint}>
                          Nothing is decided yet — you confirm or change all of this on the next
                          step.
                        </span>
                      </div>

                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Row identity</span>
                        <span className={styles.summaryValue}>
                          {recordKeyPaths.length ? (
                            <>
                              {recordKeyPaths.map((path) => (
                                <code key={path} className={styles.pathChip}>
                                  {path}
                                </code>
                              ))}
                              <em>
                                {' '}
                                recommended
                                {keyCandidates.length > 1
                                  ? ` — ${keyCandidates.length - 1} other candidate${
                                      keyCandidates.length === 2 ? '' : 's'
                                    } to choose from`
                                  : ''}
                              </em>
                            </>
                          ) : keyNeedsExplicitChoice ? (
                            <em>
                              {keyCandidates.length} candidate
                              {keyCandidates.length === 1 ? '' : 's'} found, none recommended —
                              you’ll choose one next, it’s required
                            </em>
                          ) : (
                            <em>none found — you’ll pick one next, it’s required</em>
                          )}
                        </span>
                      </div>

                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Text</span>
                        <span className={styles.summaryValue}>
                          {mapping.text.length === 0 ? (
                            <em>no text columns found — the agent would have nothing to read</em>
                          ) : (
                            <>
                              {mapping.text.length} column
                              {mapping.text.length === 1 ? '' : 's'} to store
                              <em>
                                {' '}
                                — {mapping.text.filter((t) => t.embed).length} of them suggested
                                for semantic search
                              </em>
                            </>
                          )}
                        </span>
                      </div>

                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Facets</span>
                        <span className={styles.summaryValue}>
                          {facetCandidates.length === 0 ? (
                            <em>none suggested — nothing would be filterable or countable</em>
                          ) : (
                            <>
                              {mapping.facets.length} of {facetCandidates.length} suggested
                              field
                              {facetCandidates.length === 1 ? '' : 's'} pre-selected
                              <em> — you choose which ones to keep</em>
                            </>
                          )}
                        </span>
                      </div>

                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Dates</span>
                        <span className={styles.summaryValue}>
                          {mapping.dates.length === 0 ? (
                            <em>none suggested — “last month” style questions won’t work</em>
                          ) : (
                            <>
                              <code className={styles.pathChip}>
                                {mapping.dates.find((d) => d.primary)?.path}
                              </code>
                              <em>
                                {' '}
                                suggested as the primary
                                {mapping.dates.length > 1
                                  ? `, ${mapping.dates.length} date columns in all`
                                  : ''}
                              </em>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : step === 2 ? (
            <div className={styles.form}>
              <KgCombinedFieldsStep
                drafts={combinedDrafts}
                onChange={setCombinedDrafts}
                fields={inspectFields}
                namespace={inspection?.namespace}
                inspectedCount={inspection?.inspectedCount}
              />
              {combinedError && <Alert severity="error">{combinedError}</Alert>}
            </div>
          ) : (
            <div className={styles.form}>
              {/* Always reachable, even when the step was skipped. */}
              {!hasCombinedStep && (
                <span className={styles.manualCombine}>
                  Same information stored under different field names?{' '}
                  <button type="button" className={styles.linkButton} onClick={combineManually}>
                    Combine fields manually
                  </button>
                </span>
              )}
              {/* ── Row identity ── editable here, because it is expensive to
                  change later: records already loaded keep the key they were
                  ingested with, so the next push inserts instead of updating. */}
              <div className={styles.editBlock}>
                <div className={styles.editHead}>
                  <span className={styles.editTitle}>Which column(s) identify a row?</span>
                  <span className={styles.editHint}>
                    Required. Pushing the same value again overwrites that record instead of
                    adding a second one — so a value that repeats means records are lost.
                    Uniqueness across the sample is not proof of uniqueness in your full export.
                  </span>
                </div>

                {/* No recommendation is an ANSWER, not a gap. The server
                    declining to pick means nothing in this export is a safe
                    upsert key on the evidence it has — promoting the first row
                    would be the UI choosing the customer's key for them. */}
                {keyNeedsExplicitChoice && (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    <strong>None of these is a confident choice.</strong> The inspection found{' '}
                    {keyCandidates.length} column
                    {keyCandidates.length === 1 ? '' : 's'} that could work but recommends none of
                    them — usually because the values are unique in this sample without being
                    unique in general. Pick one deliberately: if it repeats in your full export,
                    each repeat silently overwrites the record before it.
                  </Alert>
                )}

                {/* The server's shortlist, in the server's order. These are
                    ALTERNATIVES: one click REPLACES the choice rather than
                    adding to a composite key, which is what "add another
                    column" below is for. */}
                {keyCandidates.length > 0 && (
                  <div className={styles.suggestRow}>
                    <span className={styles.suggestLabel}>Suggested:</span>
                    {keyCandidates.map((candidate) => {
                      const active =
                        recordKeyPaths.length === candidate.paths.length &&
                        candidate.paths.every((path, i) => recordKeyPaths[i] === path);
                      return (
                        <Tooltip
                          key={candidate.label}
                          title={
                            candidate.uniqueInSample
                              ? `Unique across the ${formatNumber(
                                  inspection?.inspectedCount || 0
                                )} records inspected — which is not proof it stays unique in production.`
                              : `Present on ${formatCoverage(candidate.coveragePct)} of records.`
                          }
                        >
                          <button
                            type="button"
                            className={`${styles.suggestChip} ${active ? styles.suggestChipActive : ''}`}
                            onClick={() => {
                              setPendingKeyColumn(false);
                              editMapping((next) => {
                                // A candidate is a whole key — a composite one
                                // is applied in full, not one column at a time.
                                next.record_key = serializeRecordKey(candidate.paths);
                              });
                            }}
                          >
                            {active && <Check size={12} />}
                            <code>{candidate.label}</code>
                            {/* Why this one is the suggestion, rather than
                                leaving the choice unexplained. */}
                            {candidate.recommended && (
                              <em className={styles.suggestTag}>recommended</em>
                            )}
                            {!candidate.recommended && candidate.nameMatchesConvention && (
                              <em className={styles.suggestTagMuted}>looks like an id</em>
                            )}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}

                {recordKeyPaths.length === 0 && (
                  <div className={styles.editRow}>
                    <PathField
                      value=""
                      onChange={(v) => setRecordKeyAt(0, v)}
                      fields={inspectFields}
                      label="Column"
                      excludeLists
                    />
                  </div>
                )}

                {recordKeyPaths.map((path, index) => (
                  <div key={index} className={styles.editRow}>
                    <PathField
                      value={path}
                      onChange={(v) => setRecordKeyAt(index, v)}
                      fields={inspectFields}
                      label={index === 0 ? 'Column' : 'and'}
                      excludeLists
                    />
                    {recordKeyPaths.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          editMapping((next) => {
                            const list = parseRecordKey(next.record_key);
                            list.splice(index, 1);
                            next.record_key = serializeRecordKey(list);
                          })
                        }
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    )}
                  </div>
                ))}
                {pendingKeyColumn && (
                  <div className={styles.editRow}>
                    <PathField
                      value=""
                      onChange={(v) => {
                        if (!v) return;
                        // Appends: index === current length.
                        setRecordKeyAt(recordKeyPaths.length, v);
                        setPendingKeyColumn(false);
                      }}
                      fields={inspectFields}
                      label="and"
                      placeholder="Pick the next column"
                      excludeLists
                    />
                    <IconButton
                      size="small"
                      onClick={() => setPendingKeyColumn(false)}
                      aria-label="Cancel this column"
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                )}

                {/* "Another" needs a first — the empty-key case already shows a
                    blank row above. One open row at a time keeps a composite
                    key readable while it is being built. */}
                {recordKeyPaths.length > 0 && !pendingKeyColumn && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Plus size={14} />}
                    onClick={() => setPendingKeyColumn(true)}
                  >
                    Add another column
                  </Button>
                )}
                {recordKeyError && <span className={styles.editError}>{recordKeyError}</span>}
                {/* Repeated identities are reported by the dry-run below, over
                    the same records — one place, so the two can never disagree. */}
              </div>

              {/* ── Text ── the one setting that CANNOT be changed after loading,
                  so it has to be decided before the dataset exists.

                  Two decisions per field, deliberately separate:
                    • INCLUDE — is this text stored on the record at all? Stored
                      text is readable by the agent after a filter narrows things
                      down, and costs nothing.
                    • EMBED — is it also vectorised? That is the money setting.
                  A big body defaults to included-but-not-embedded, because at
                  scale that is the difference between roughly $150 and $975. */}
              <div className={styles.editBlock}>
                <div className={styles.editHead}>
                  <span className={styles.editTitle}>Which text should be stored?</span>
                  <span className={styles.editHint}>
                    <Lock size={11} /> The one choice you cannot change after loading records —
                    the original text isn’t kept, so changing it later means starting the dataset
                    again.
                  </span>
                </div>

                {textCandidates.length === 0 ? (
                  <span className={styles.editError}>
                    No text columns were found, so the agent will have nothing to read or quote.
                    You can add them in the mapping editor after creating.
                  </span>
                ) : (
                  <div className={styles.textPickList}>
                    {textCandidates.map((candidate) => {
                      const chosen = chosenText.get(candidate.path);
                      const included = Boolean(chosen);
                      return (
                        <div
                          key={candidate.path}
                          className={`${styles.textPickRow} ${included ? styles.textPickRowOn : ''}`}
                        >
                          <button
                            type="button"
                            className={styles.textPickToggle}
                            onClick={() => toggleTextField(candidate.path)}
                            aria-pressed={included}
                          >
                            <span
                              className={`${styles.facetPickBox} ${included ? styles.facetPickBoxOn : ''}`}
                            >
                              {included && <Check size={12} strokeWidth={3} />}
                            </span>
                            <span className={styles.textPickMain}>
                              <span className={styles.textPickName}>
                                {candidate.label}
                                <code>{candidate.path}</code>
                                {candidate.recommended && (
                                  <em className={styles.textPickTag}>title</em>
                                )}
                                {candidate.large && (
                                  <em className={styles.textPickTagLarge}>large</em>
                                )}
                              </span>
                              <span className={styles.textPickMeta}>
                                <span className={styles[coverageTone(candidate.coveragePct)]}>
                                  {formatCoverage(candidate.coveragePct)} populated
                                </span>
                                {candidate.maxCharsSeen > 0 && (
                                  <span>
                                    up to {formatNumber(candidate.maxCharsSeen)} characters
                                  </span>
                                )}
                              </span>
                            </span>
                          </button>

                          {included && (
                            <div className={styles.textPickEmbed}>
                              <Tooltip
                                title={
                                  chosen.embed
                                    ? 'Vectorised — findable by meaning, and billed for.'
                                    : 'Stored on the record and readable by the agent after a filter narrows things down. Costs nothing to keep.'
                                }
                              >
                                <span className={styles.textPickEmbedState}>
                                  {chosen.embed
                                    ? 'Searchable by meaning'
                                    : 'Stored and readable, not searchable by meaning'}
                                </span>
                              </Tooltip>
                              <Switch
                                size="small"
                                checked={Boolean(chosen.embed)}
                                onChange={() => toggleTextEmbed(candidate.path)}
                              />
                            </div>
                          )}

                          {/* The cost lever, explained where the decision is
                              made rather than in a doc nobody opens. */}
                          {included && candidate.large && !chosen.embed && (
                            <span className={styles.textPickNote}>
                              Left unembedded because it is long. The agent can still read it —
                              it filters first, then opens the record — and this is what keeps a
                              large dataset affordable. Turn it on if “what did this say about
                              X?” has to match the body text.
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {chosenText.size === 0 && textCandidates.length > 0 && (
                  <span className={styles.editError}>
                    Nothing is stored, so the agent has no text to read or quote — it could only
                    count and filter these records.
                  </span>
                )}
                {chosenText.size > 0 && ![...chosenText.values()].some((t) => t.embed) && (
                  <span className={styles.textPickWarn}>
                    Nothing is embedded. Filters and counts will work, but semantic search over
                    this dataset will return nothing.
                  </span>
                )}
                {/* The server's hard cap. Caught here rather than as a 400 on
                    Create, which is where a 48-field selection surfaced before. */}
                {chosenText.size > MAX_TEXT_FIELDS && (
                  <span className={styles.textPickWarn}>
                    {chosenText.size} selected — the maximum is {MAX_TEXT_FIELDS}. Untick{' '}
                    {chosenText.size - MAX_TEXT_FIELDS}; each one is assembled into the document
                    the model reads, so a shorter list also answers better.
                  </span>
                )}
              </div>

              {/* ── The rest, at a glance. All editable after creating. ── */}
              <div className={styles.summary}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Title</span>
                  <span className={styles.summaryValue}>
                    {mapping.title ? (
                      <code className={styles.pathChip}>{mapping.title}</code>
                    ) : (
                      <em>none</em>
                    )}
                  </span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Facets</span>
                  <span className={styles.summaryValue}>
                    {mapping.facets.length} filterable field
                    {mapping.facets.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Calls records</span>
                  <span className={styles.summaryValue}>
                    “{mapping.dataset.record_label_plural}” — what the agent says in its answers
                  </span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel} />
                  <span className={styles.summaryNote}>
                    Facets, dates and labels can all be changed after creating — this step only
                    asks about the two that are costly to undo.
                  </span>
                </div>
              </div>

              {/* ── Facets ── offered, not pre-ticked.
                  The inspection proposes every field that COULD be filtered on.
                  Taking all of them gives the model a catalog where the row
                  identity, two raw timestamps and two copies of the same
                  taxonomy sit alongside the three filters that matter — each one
                  costing prompt space and making the useful ones harder to find.
                  So they are listed with the evidence needed to choose. */}
              {facetCandidates.length > 0 && (
                <div className={styles.editBlock}>
                  <div className={styles.editHead}>
                    <span className={styles.editTitle}>What should be filterable?</span>
                    <span className={styles.editHint}>
                      Each one ticked becomes a filter the agent can count and group by, by
                      name. The recommended set is already selected — untick anything you don’t
                      want, and tick any of the rest. A handful of good facets beats twenty. All
                      of this stays editable afterwards.
                    </span>
                  </div>

                  <div className={styles.facetPickHead}>
                    <span className={styles.facetPickCount}>
                      {chosenFacets.size === 0 ? (
                        <em>none selected — the agent won’t be able to filter or count</em>
                      ) : chosenFacets.size > MAX_FACETS ? (
                        <em>
                          {chosenFacets.size} selected — {MAX_FACETS} is the maximum, drop{' '}
                          {chosenFacets.size - MAX_FACETS}
                        </em>
                      ) : (
                        `${chosenFacets.size} of ${facetCandidates.length} selected`
                      )}
                    </span>
                    {recommendedFacets.length > 0 && !recommendedFacetsChosen && (
                      <Button size="small" variant="text" onClick={chooseSuggestedFacets}>
                        Reset to the {Math.min(recommendedFacets.length, MAX_FACETS)} recommended
                      </Button>
                    )}
                  </div>

                  {facetCandidates.length > 12 && (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={`Filter ${formatNumber(facetCandidates.length)} candidates`}
                      value={facetQuery}
                      onChange={(e) => setFacetQuery(e.target.value)}
                    />
                  )}

                  {facetQueryNorm && (
                    <span className={styles.facetPickFilterNote}>
                      {formatNumber(visibleFacetCandidates.length)} match “{facetQuery.trim()}”
                      {hiddenChosenCount > 0 &&
                        ` · ${hiddenChosenCount} selected facet${hiddenChosenCount === 1 ? '' : 's'} hidden by the filter (still selected)`}
                      {' · '}
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => setFacetQuery('')}
                      >
                        clear
                      </button>
                    </span>
                  )}

                  <div className={styles.facetPickList}>
                    {visibleFacetCandidates.length === 0 && (
                      <span className={styles.facetPickEmpty}>
                        No candidate matches “{facetQuery}”.
                      </span>
                    )}
                    {visibleFacetCandidates.map((candidate) => {
                      const checked = chosenFacets.has(candidate.path);
                      return (
                        <button
                          type="button"
                          key={candidate.path}
                          className={`${styles.facetPickRow} ${checked ? styles.facetPickRowOn : ''} ${
                            candidate.reason ? styles.facetPickRowWeak : ''
                          }`}
                          onClick={() => toggleFacet(candidate.path)}
                          aria-pressed={checked}
                        >
                          <span
                            className={`${styles.facetPickBox} ${checked ? styles.facetPickBoxOn : ''}`}
                          >
                            {checked && <Check size={12} strokeWidth={3} />}
                          </span>
                          <span className={styles.facetPickMain}>
                            <span className={styles.facetPickName}>
                              {candidate.label}
                              <code>{candidate.name}</code>
                              {candidate.cardinality === 'many' && (
                                <em className={styles.facetPickMulti}>many</em>
                              )}
                            </span>
                            {/* Coverage next to every count, everywhere — a facet
                                on 12% of records answers for 12% of them. */}
                            <span className={styles.facetPickMeta}>
                              <span className={styles[coverageTone(candidate.coveragePct)]}>
                                {formatCoverage(candidate.coveragePct)} populated
                              </span>
                              {candidate.distinct > 0 && (
                                <span>
                                  {formatNumber(candidate.distinct)} distinct value
                                  {candidate.distinct === 1 ? '' : 's'}
                                </span>
                              )}
                              {candidate.values.length > 0 && (
                                <span className={styles.facetPickValues}>
                                  {candidate.values
                                    .slice(0, 3)
                                    .map((v) => truncateValue(String(v), 22))
                                    .join(' · ')}
                                </span>
                              )}
                            </span>
                            {candidate.reason && (
                              <span className={styles.facetPickReason}>{candidate.reason}</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Timezone ── one control for every date, because it is one
                  decision, and the consequence of getting it wrong is invisible:
                  a record at 2026-01-31 20:00 UTC is FEBRUARY in IST, and
                  "articles from February" has to mean February to the reader. */}
              {(mapping.dates || []).length > 0 && (
                <div className={styles.editBlock}>
                  <div className={styles.editHead}>
                    <span className={styles.editTitle}>
                      <Clock size={13} /> Which timezone are these dates in?
                    </span>
                    <span className={styles.editHint}>
                      Decides which month a record falls in, and so which records answer “last
                      month”. Changeable later, but every record already loaded keeps the
                      bucketing it came in with.
                    </span>
                  </div>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={timezone}
                    onChange={(e) => applyTimezone(Number(e.target.value))}
                  >
                    {TIMEZONE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              )}

              {/* ── Dry-run ── */}
              {dryRunning ? (
                <div className={styles.dryRunLoading}>
                  <CircularProgress size={18} />
                  <span>
                    Checking this mapping against {formatNumber(
                      Math.min(samples.length, DRY_RUN_SAMPLE_SIZE)
                    )}{' '}
                    of your records…
                  </span>
                </div>
              ) : dryRunError ? (
                <Alert severity="warning">
                  {dryRunError}
                  <br />
                  You can still create the dataset and fix the mapping afterwards.
                </Alert>
              ) : (
                <KgDryRunReport result={dryRun} totalFacets={mapping.facets.length} />
              )}

              {/* ── Advanced: creation-only, unchangeable later ── */}
              <button
                type="button"
                className={styles.advancedToggle}
                onClick={() => setShowAdvanced((v) => !v)}
              >
                <ChevronDown
                  size={14}
                  style={{ transform: showAdvanced ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                />
                Advanced
              </button>
              {showAdvanced && (
                <div className={styles.advancedBlock}>
                  <TextField
                    select
                    size="small"
                    label="Embedding width"
                    value={embeddingDimensions}
                    onChange={(e) => setEmbeddingDimensions(Number(e.target.value))}
                    fullWidth
                    helperText="Set once, at creation — changing it later would mean re-embedding everything. 512 is the right answer for almost everyone."
                  >
                    <MenuItem value={512}>512 — recommended</MenuItem>
                    <MenuItem value={1024}>1024</MenuItem>
                    <MenuItem value={1536}>1536</MenuItem>
                    <MenuItem value={3072}>3072 — ~6× the storage and cost</MenuItem>
                  </TextField>
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => onClose()} variant="text" className={styles.cancelButton}>
            Cancel
          </Button>
          {step === 0 ? (
            <Tooltip title={step0Valid ? '' : 'Add a name and a sample file to continue.'}>
              <span>
                <Button
                  onClick={goToInspect}
                  variant="contained"
                  className={styles.saveButton}
                  disabled={!step0Valid}
                >
                  Inspect my data
                </Button>
              </span>
            </Tooltip>
          ) : step === 1 ? (
            <Tooltip title={dryRunBlocked || ''}>
              <span>
                <Button
                  onClick={leaveInspectStep}
                  variant="contained"
                  className={styles.saveButton}
                  disabled={inspecting || !mapping}
                >
                  {hasCombinedStep
                    ? 'Next: combined fields'
                    : dryRunBlocked
                      ? 'Pick a row identity'
                      : 'Use this mapping'}
                </Button>
              </span>
            </Tooltip>
          ) : step === 2 ? (
            <Tooltip title={combinedBlocked ? 'Fix the highlighted combined fields first.' : ''}>
              <span>
                <Button
                  onClick={applyCombined}
                  variant="contained"
                  className={styles.saveButton}
                  disabled={applyingCombined || combinedBlocked}
                  startIcon={applyingCombined ? <CircularProgress size={14} /> : null}
                >
                  {applyingCombined ? 'Applying…' : 'Continue'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={dryRunBlocked || ''}>
              <span>
                <Button
                  onClick={handleCreate}
                  variant="contained"
                  className={styles.saveButton}
                  disabled={submitting || dryRunning || Boolean(dryRunBlocked)}
                  startIcon={submitting ? <CircularProgress size={14} /> : null}
                >
                  {submitting ? 'Creating…' : 'Create dataset'}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default KgCreateDatasetDialog;
